import { ApplicabilityDecision, WorkspaceProfile, AuditEntry, DecisionStatus } from '../types/decision';
import { SyncAdapterConfig } from '../types/sync';
import { ArchitectureSystem } from '../types/system';
import { MasterDataPackage } from '../types/masterData';
import { getDefaultMasterData } from './defaultMasterData';

const DB_NAME = 'DPDP_Obligations_DB';
const DB_VERSION = 3;

const DEFAULT_WORKSPACE: WorkspaceProfile = {
  id: 'ws_default',
  name: 'Primary Enterprise Assessment',
  entity_name: 'Acme FinTech Technologies Private Limited',
  trade_brand_name: 'AcmePay & AcmeWealth',
  entity_type: 'Private Limited Company',
  cin_registration: 'U72200MH2023PTC123456',
  pan_tan: 'AAACA1234A',
  gstin: '27AAACA1234A1Z5',
  industry_sector: 'Fintech & Financial Services (BFSI)',
  incorporation_date: '2020-04-15',
  registered_address: 'Level 14, Tower B, Cyber City, Bandra Kurla Complex (BKC), Bandra East, Mumbai, Maharashtra 400051, India',
  headquarters_address: 'Level 14, Tower B, Cyber City, Bandra Kurla Complex (BKC), Bandra East, Mumbai, Maharashtra 400051, India',
  corporate_website: 'https://acmepay.in',
  corporate_email: 'compliance@acmepay.in',
  corporate_phone: '+91 22 6123 4567',
  dpo_name: 'Adv. Rajesh Sharma',
  dpo_designation: 'Data Protection Officer & Head of Privacy Compliance',
  dpo_email: 'dpo@acmepay.in',
  dpo_phone: '+91 98765 43210',
  dpo_office_address: 'Level 14, Tower B, Cyber City, BKC, Mumbai 400051, India',
  grievance_officer_name: 'Priya Iyer',
  grievance_officer_designation: 'Head of Customer Grievance Redressal (s.8(10))',
  grievance_officer_email: 'grievance@acmepay.in',
  grievance_officer_phone: '1800-123-4567',
  grievance_portal_url: 'https://acmepay.in/privacy-grievance',
  authorized_signatory_name: 'Vikramaditya Singhania',
  authorized_signatory_designation: 'Managing Director & Chief Executive Officer',
  authorized_signatory_email: 'ceo@acmepay.in',
  is_sdf: true,
  sdf_factors: [
    'High volume and velocity of personal data processed',
    'Risk of harm to Data Principals / sensitive profiling'
  ],
  sdf_gazette_notification: 'MeitY Notif. S.O. 412(E) / 2026',
  processes_children_data: true,
  parental_consent_mechanism: 'DigiLocker Parent Token & Aadhaar Virtual ID Tokenization',
  age_gating_mechanism: 'Government Token & Credit Card Verification Gate',
  processes_disabilities_data: false,
  guardian_consent_mechanism: 'Lawful Guardian Certificate via Legal Power of Attorney',
  has_cross_border_transfers: true,
  cross_border_safeguards: 'Standard Contractual Clauses & Negative List Whitelist Compliance',
  cross_border_countries: 'Singapore (AWS AP-Southeast-1), United States (Enterprise GSuite)',
  uses_consent_managers: true,
  consent_manager_providers: 'Setu Consent Manager (MeitY Registered), OneMoney AA',
  estimated_data_principals: '2,500,000+ registered active users',
  data_principal_categories: ['Retail Banking Customers', 'Merchant Partners', 'Employees & Contractors'],
  total_data_processors_count: 12,
  created_at: new Date().toISOString(),
  updated_at: new Date().toISOString(),
};

const DEFAULT_SYNC_CONFIG: SyncAdapterConfig = {
  adapter_type: 'local_file',
  endpoint_url: '',
  api_key: '',
};

function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    if (typeof window === 'undefined' || !window.indexedDB) {
      reject(new Error('IndexedDB not supported'));
      return;
    }

    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = (event: IDBVersionChangeEvent) => {
      const db = (event.target as IDBOpenDBRequest).result;

      if (!db.objectStoreNames.contains('workspaces')) {
        db.createObjectStore('workspaces', { keyPath: 'id' });
      }
      if (!db.objectStoreNames.contains('decisions')) {
        const store = db.createObjectStore('decisions', { keyPath: 'composite_key' });
        store.createIndex('workspace_id', 'workspace_id', { unique: false });
        store.createIndex('target_id', 'target_id', { unique: false });
      }
      if (!db.objectStoreNames.contains('systems')) {
        const store = db.createObjectStore('systems', { keyPath: 'composite_key' });
        store.createIndex('workspace_id', 'workspace_id', { unique: false });
      }
      if (!db.objectStoreNames.contains('master_data')) {
        db.createObjectStore('master_data', { keyPath: 'workspace_id' });
      }
      if (!db.objectStoreNames.contains('audit_trail')) {
        const store = db.createObjectStore('audit_trail', { keyPath: 'id' });
        store.createIndex('workspace_id', 'workspace_id', { unique: false });
      }
      if (!db.objectStoreNames.contains('settings')) {
        db.createObjectStore('settings', { keyPath: 'key' });
      }
    };

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

// Fallback localStorage keys
const LS_PREFIX = 'dpdp_obl_';

export async function getActiveWorkspace(): Promise<WorkspaceProfile> {
  try {
    const db = await openDB();
    return new Promise((resolve) => {
      const tx = db.transaction(['workspaces', 'settings'], 'readonly');
      const settingsStore = tx.objectStore('settings');
      const getActive = settingsStore.get('active_workspace_id');

      getActive.onsuccess = () => {
        const activeId = getActive.result?.value || DEFAULT_WORKSPACE.id;
        const wsStore = tx.objectStore('workspaces');
        const getWs = wsStore.get(activeId);

        getWs.onsuccess = () => {
          if (getWs.result) {
            resolve(getWs.result);
          } else {
            saveWorkspace(DEFAULT_WORKSPACE).then(() => resolve(DEFAULT_WORKSPACE));
          }
        };
        getWs.onerror = () => resolve(DEFAULT_WORKSPACE);
      };
      getActive.onerror = () => resolve(DEFAULT_WORKSPACE);
    });
  } catch (err) {
    const saved = localStorage.getItem(`${LS_PREFIX}active_ws`);
    if (saved) {
      try { return JSON.parse(saved); } catch {}
    }
    return DEFAULT_WORKSPACE;
  }
}

export async function saveWorkspace(ws: WorkspaceProfile): Promise<void> {
  ws.updated_at = new Date().toISOString();
  try {
    const db = await openDB();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(['workspaces', 'settings'], 'readwrite');
      tx.objectStore('workspaces').put(ws);
      tx.objectStore('settings').put({ key: 'active_workspace_id', value: ws.id });
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  } catch (err) {
    localStorage.setItem(`${LS_PREFIX}active_ws`, JSON.stringify(ws));
  }
}

// -------------------------------------------------------------
// MASTER DATA MANAGEMENT (ACTORS, DOMAINS, TRANCHES, CONTROLS, PROVISIONS)
// -------------------------------------------------------------

export async function getMasterData(workspaceId: string): Promise<MasterDataPackage> {
  try {
    const db = await openDB();
    return new Promise((resolve) => {
      const tx = db.transaction('master_data', 'readonly');
      const req = tx.objectStore('master_data').get(workspaceId);

      req.onsuccess = () => {
        if (req.result && req.result.data) {
          const data = req.result.data;
          if (!data.users || !Array.isArray(data.users)) {
            data.users = getDefaultMasterData().users;
          }
          if (!data.exceptions || !Array.isArray(data.exceptions)) {
            data.exceptions = getDefaultMasterData().exceptions;
          }
          resolve(data);
        } else {
          const defaults = getDefaultMasterData();
          saveMasterData(workspaceId, defaults).then(() => resolve(defaults));
        }
      };
      req.onerror = () => resolve(getDefaultMasterData());
    });
  } catch (err) {
    const saved = localStorage.getItem(`${LS_PREFIX}master_data_${workspaceId}`);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (!parsed.users || !Array.isArray(parsed.users)) {
          parsed.users = getDefaultMasterData().users;
        }
        if (!parsed.exceptions || !Array.isArray(parsed.exceptions)) {
          parsed.exceptions = getDefaultMasterData().exceptions;
        }
        return parsed;
      } catch {}
    }
    return getDefaultMasterData();
  }
}

export async function saveMasterData(workspaceId: string, data: MasterDataPackage): Promise<void> {
  data.updated_at = new Date().toISOString();
  try {
    const db = await openDB();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction('master_data', 'readwrite');
      tx.objectStore('master_data').put({ workspace_id: workspaceId, data });
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  } catch (err) {
    localStorage.setItem(`${LS_PREFIX}master_data_${workspaceId}`, JSON.stringify(data));
  }
}

export async function resetMasterDataToDefaults(workspaceId: string): Promise<MasterDataPackage> {
  const defaults = getDefaultMasterData();
  await saveMasterData(workspaceId, defaults);
  return defaults;
}

export async function getDecisions(workspaceId: string): Promise<Record<string, ApplicabilityDecision>> {
  try {
    const db = await openDB();
    return new Promise((resolve) => {
      const tx = db.transaction('decisions', 'readonly');
      const index = tx.objectStore('decisions').index('workspace_id');
      const req = index.getAll(workspaceId);

      req.onsuccess = () => {
        const map: Record<string, ApplicabilityDecision> = {};
        for (const item of req.result) {
          map[item.target_id] = item.decision;
        }
        resolve(map);
      };
      req.onerror = () => resolve({});
    });
  } catch (err) {
    const saved = localStorage.getItem(`${LS_PREFIX}decisions_${workspaceId}`);
    if (saved) {
      try { return JSON.parse(saved); } catch {}
    }
    return {};
  }
}

export async function saveDecision(
  workspaceId: string,
  decision: ApplicabilityDecision,
  actorName: string = 'Compliance Officer'
): Promise<void> {
  const composite_key = `${workspaceId}::${decision.target_id}`;
  decision.updated_at = new Date().toISOString();
  decision.rev = (decision.rev || 0) + 1;

  const auditEntry: AuditEntry = {
    id: `audit_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`,
    workspace_id: workspaceId,
    target_id: decision.target_id,
    previous_status: 'UNASSESSED',
    new_status: decision.status,
    actor: actorName,
    notes: decision.rationale || 'Decision updated in register',
    timestamp: new Date().toISOString(),
  };

  try {
    const db = await openDB();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(['decisions', 'audit_trail'], 'readwrite');
      tx.objectStore('decisions').put({
        composite_key,
        workspace_id: workspaceId,
        target_id: decision.target_id,
        decision
      });
      tx.objectStore('audit_trail').put(auditEntry);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  } catch (err) {
    const current = await getDecisions(workspaceId);
    current[decision.target_id] = decision;
    localStorage.setItem(`${LS_PREFIX}decisions_${workspaceId}`, JSON.stringify(current));
  }
}

export async function bulkSaveDecisions(
  workspaceId: string,
  decisions: Record<string, ApplicabilityDecision>
): Promise<void> {
  try {
    const db = await openDB();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction('decisions', 'readwrite');
      const store = tx.objectStore('decisions');
      for (const [target_id, decision] of Object.entries(decisions)) {
        store.put({
          composite_key: `${workspaceId}::${target_id}`,
          workspace_id: workspaceId,
          target_id,
          decision
        });
      }
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  } catch (err) {
    localStorage.setItem(`${LS_PREFIX}decisions_${workspaceId}`, JSON.stringify(decisions));
  }
}

// -------------------------------------------------------------
// CUSTOM ARCHITECTURE SYSTEMS INVENTORY
// -------------------------------------------------------------

export async function getCustomSystems(workspaceId: string): Promise<ArchitectureSystem[]> {
  try {
    const master = await getMasterData(workspaceId);
    return master.systems || [];
  } catch (err) {
    return getDefaultMasterData().systems;
  }
}

export async function saveCustomSystem(workspaceId: string, system: ArchitectureSystem): Promise<void> {
  const master = await getMasterData(workspaceId);
  const existingIdx = (master.systems || []).findIndex(s => s.id === system.id);
  if (existingIdx >= 0) {
    master.systems[existingIdx] = system;
  } else {
    master.systems = [...(master.systems || []), system];
  }
  await saveMasterData(workspaceId, master);
}

export async function deleteCustomSystem(workspaceId: string, systemId: string): Promise<void> {
  const master = await getMasterData(workspaceId);
  master.systems = (master.systems || []).filter(s => s.id !== systemId);
  await saveMasterData(workspaceId, master);
}

export async function getAuditTrail(workspaceId: string): Promise<AuditEntry[]> {
  try {
    const db = await openDB();
    return new Promise((resolve) => {
      const tx = db.transaction('audit_trail', 'readonly');
      const index = tx.objectStore('audit_trail').index('workspace_id');
      const req = index.getAll(workspaceId);
      req.onsuccess = () => resolve(req.result || []);
      req.onerror = () => resolve([]);
    });
  } catch (err) {
    return [];
  }
}

export async function getSyncConfig(): Promise<SyncAdapterConfig> {
  try {
    const db = await openDB();
    return new Promise((resolve) => {
      const tx = db.transaction('settings', 'readonly');
      const req = tx.objectStore('settings').get('sync_config');
      req.onsuccess = () => resolve(req.result?.value || DEFAULT_SYNC_CONFIG);
      req.onerror = () => resolve(DEFAULT_SYNC_CONFIG);
    });
  } catch (err) {
    const saved = localStorage.getItem(`${LS_PREFIX}sync_config`);
    if (saved) {
      try { return JSON.parse(saved); } catch {}
    }
    return DEFAULT_SYNC_CONFIG;
  }
}

export async function saveSyncConfig(config: SyncAdapterConfig): Promise<void> {
  try {
    const db = await openDB();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction('settings', 'readwrite');
      tx.objectStore('settings').put({ key: 'sync_config', value: config });
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  } catch (err) {
    localStorage.setItem(`${LS_PREFIX}sync_config`, JSON.stringify(config));
  }
}
